import { Maybe, UploadFile } from '@ts/operations'
import Head from 'next/head'
import React from 'react'

import { useSiteConfig } from '../../lib/siteconfig'

type SeoTags = Partial<{
  defaultImage: Maybe<Partial<UploadFile>>
  description: Maybe<string>
  facebookDescription: Maybe<string>
  facebookImage: Maybe<Partial<UploadFile>>
  facebookTitle: Maybe<string>
  noindex: Maybe<boolean>
  title: Maybe<string>
  twitterDescription: Maybe<string>
  twitterImage: Maybe<Partial<UploadFile>>
  twitterTitle: Maybe<string>
}>

const MetaTags = ({ seoTags, title }: { seoTags?: SeoTags; title?: string }) => {
  const { metaData } = useSiteConfig()

  const siteTitle = metaData?.siteTitle ? ` | ${metaData?.siteTitle}` : ''
  const seoTitle = seoTags?.title || `${title || ''}${siteTitle}`
  const description = seoTags?.description || metaData?.siteDescription
  const defaultImage = seoTags?.defaultImage?.url || metaData?.image?.url
  const facebookId = metaData?.facebookId
  const facebookPublisher =
    metaData?.facebookPublisher && `https://www.facebook.com/${metaData?.facebookPublisher}`
  const facebookTitle = seoTags?.facebookTitle || seoTitle
  const facebookDescription = seoTags?.facebookDescription || description
  const facebookImage = seoTags?.facebookImage?.url || defaultImage
  const twitterId = metaData?.twitterId
  const twitterCreator = metaData?.twitterCreator
  const twitterTitle = seoTags?.twitterTitle || seoTitle
  const twitterDescription = seoTags?.twitterDescription || description
  const twitterImage = seoTags?.twitterImage?.url || defaultImage
  const noindex = seoTags?.noindex

  return (
    <Head>
      <title>{seoTitle}</title>
      {description && <meta content={description} name="description" />}
      <meta content="en_US" property="og:locale" />
      <meta content="article" property="og:type" />
      {metaData?.siteTitle && <meta content={metaData?.siteTitle} property="og:site_name" />}
      {facebookId && <meta content={facebookId} property="fb:pages" />}
      {facebookPublisher && <meta content={facebookPublisher} property="article:publisher" />}
      {facebookTitle && <meta content={facebookTitle} property="og:title" />}
      {facebookDescription && <meta content={facebookDescription} property="og:description" />}
      {facebookImage && <meta content={facebookImage} property="og:image" />}
      {facebookImage && <meta content={facebookImage} property="og:image:secure_url" />}
      {facebookImage && <meta content="650" property="og:image:width" />}
      {facebookImage && <meta content="433" property="og:image:height" />}
      {twitterDescription && <meta content={twitterDescription} name="twitter:description" />}
      {twitterTitle && <meta content={twitterTitle} name="twitter:title" />}
      {twitterId && <meta content={twitterId} name="twitter:site" />}
      {twitterImage && (
        <meta
          content={`${twitterImage}?${Math.floor(Math.random() * 1000000)}`}
          name="twitter:image"
        />
      )}
      {twitterImage && <meta content="summary_large_image" name="twitter:card" />}
      {twitterCreator && <meta content={twitterCreator} name="twitter:creator" />}
      {noindex && <meta content="noindex" name="robots" />}
    </Head>
  )
}

export default MetaTags
